<template>
  <div class="advance-reporting-popup">
    <ErrorHandler
      v-if="state.loadingError != null"
      :error="state.loadingError"
      @done="state.loadingError = null"
    />
    <template v-if="state.loading">
      <div class="d-flex justify-content-center">
        <CSpinner color="primary" />
      </div>
    </template>
    <template v-else>
      <div class="popup-content">
        <!-- Row 1: Format & School -->
        <div class="form-row">
          <div class="field-group">
            <label class="field-label">Format</label>
            <div class="advance-reporting-radio-group">
              <div
                v-for="format in REPORT_FORMATS"
                :key="format.id"
                class="radio-option"
              >
                <div class="radio-container">
                  <input
                    type="radio"
                    :id="format.id"
                    :value="format.id"
                    v-model="state.selectedFormat"
                    class="radio-input"
                  />
                  <div
                    class="radio-visual"
                    :class="{ checked: state.selectedFormat === format.id }"
                    @click="state.selectedFormat = format.id"
                  >
                    <div
                      class="radio-dot"
                      v-if="state.selectedFormat === format.id"
                    ></div>
                  </div>
                </div>
                <label :for="format.id" class="radio-label">{{
                  format.name
                }}</label>
              </div>
            </div>
          </div>

          <div class="field-group">
            <label class="field-label">School</label>
            <SchoolMultiSelect
              :schools="schools"
              :model-value="state.selectedSchools"
              placeholder="Select school(s)"
              @update:model-value="handleSchoolChange"
            />
          </div>
        </div>

        <!-- Row 2: Start Date & Repetition -->
        <div class="form-row">
          <div class="field-group">
            <Label class="field-label">Start Date</Label>
            <CustomDatePicker
              v-model="state.startDate"
              @update:model-value="handleStartDateChange"
              :masks="{ L: 'MM/DD/YYYY' }"
              :min-date="minStartDate"
              placeholder="Select Start Date"
              :invalid-feedback="errors.startDate"
              input-class="d-block"
            />
          </div>

          <div class="field-group">
            <div class="field-label-container">
              <label class="field-label">Repetition</label>
              <VTooltip>
                <template #popper>
                  <div>
                    Select the frequency you would like a report sent to you.
                  </div>
                </template>
                <div class="field-info-icon">
                  <i class="ri-information-line"></i>
                </div>
              </VTooltip>
            </div>
            <select
              v-model="state.selectedRepetition"
              class="advance-reporting-select-input"
            >
              <option
                v-for="option in REPETITION_OPTIONS"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Row 3: End Date & Send to (superadmin) -->
        <div class="form-row">
          <div class="field-group">
            <Label class="field-label">End Date</Label>
            <CustomDatePicker
              v-model="state.endDate"
              :masks="{ L: 'MM/DD/YYYY' }"
              :min-date="minEndDate"
              :max-date="maxEndDate"
              placeholder="Select End Date"
              :invalid-feedback="errors.endDate"
              input-class="d-block"
            />
          </div>

          <div v-if="isSuperAdmin" class="field-group send-to-field-group">
            <label class="field-label">Send to:</label>
            <div class="send-to-email-group">
              <input
                v-model="state.sendTo"
                type="text"
                class="send-to-input"
                placeholder=""
                data-testid="send-to-input"
                :aria-label="'Send to email prefix before @securly.com'"
              />
              <span class="send-to-suffix">@securly.com</span>
            </div>
            <div v-if="errors.sendTo" class="invalid-message text-start px-0">
              {{ errors.sendTo }}
            </div>
          </div>
          <div v-else class="field-group"></div>
        </div>

        <!-- Informational Text -->
        <div class="info-text">
          Your next report will be delivered on {{ nextDeliveryDate }}. After
          which reports will be sent {{ selectedRepetitionName.toLowerCase() }}.
        </div>

        <!-- Data Points Selection -->
        <div v-if="allChartList.length > 0" class="section">
          <div class="fieldset-title">
            <span>SELECT data points</span>
          </div>
          <div
            v-if="errors.selectedCharts"
            class="invalid-message text-start px-0"
          >
            {{ errors.selectedCharts }}
          </div>
          <div
            class="advance-reporting-select-all-container"
            :class="{ 'select-all-container--disabled': isSuperAdmin }"
          >
            <div class="advance-reporting-checkbox-container">
              <input
                type="checkbox"
                :id="'select-all'"
                :checked="isAllChartsSelected"
                :disabled="isSuperAdmin"
                @change="toggleSelectAll"
                class="checkbox-input"
              />
              <div
                class="checkbox-visual"
                :class="{ checked: isAllChartsSelected }"
                role="button"
                @click="toggleSelectAll"
              >
                <i class="ri-check-line" v-if="isAllChartsSelected"></i>
              </div>
            </div>
            <label :for="'select-all'" class="advance-reporting-checkbox-label"
              >Select All</label
            >
          </div>

          <div class="advance-reporting-data-point-grid">
            <ChartDataPointComponent
              v-for="chart in allChartList"
              :key="chart.feChartId"
              :chart-id="chart.id"
              :chart-name="chart.name"
              :is-selected="state.selectedCharts.includes(chart.id)"
              :checkbox-id="chart.id"
              :is-disabled="
                isSuperAdmin && chart.feChartId !== superAdminAllowedChartId
              "
              @toggle="(chartId, checked) => toggleChart(chartId, checked)"
              @click="
                (chartId) =>
                  toggleChart(chartId, !state.selectedCharts.includes(chartId))
              "
            />
          </div>
        </div>
      </div>

      <ErrorHandler
        v-if="state.savingError != null"
        class="my-2"
        :error="state.savingError"
        @done="state.savingError = null"
      />

      <div v-if="allChartList.length > 0" class="popup-footer">
        <BaseButton rounded @click="handleCancel">Cancel</BaseButton>
        <LoadingButton
          :is-loading="state.saving"
          rounded
          solid
          @click="handleSchedule"
        >
          {{ state.saving ? "Scheduling..." : "OK" }}
        </LoadingButton>
      </div>
    </template>
  </div>
</template>

<script>
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import passHelpers from "@/helpers/index"
import ChartDataPointComponent from "@/v3components/AdvanceReporting/ChartDataPointComponent.vue"
import SchoolMultiSelect from "@/v3components/AdvanceReporting/SchoolMultiSelect.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import CustomDatePicker from "@/v3components/shared/Form/CustomDatePicker.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import useVuelidate from "@vuelidate/core"
import { email, helpers, required } from "@vuelidate/validators"
import moment from "moment-timezone"
import { computed, onMounted, onUnmounted, reactive, ref } from "vue"
import { useToast } from "vue-toastification"
import { useStore } from "vuex"

export default {
  name: "ScheduleReportPopUp",
  components: {
    BaseButton,
    LoadingButton,
    ChartDataPointComponent,
    SchoolMultiSelect,
    ErrorHandler,
    CustomDatePicker,
    Label
  },
  emits: ["schedule", "cancel"],
  setup(props, { emit }) {
    const store = useStore()
    const toast = useToast()

    const SUPER_ADMIN_ALLOWED_CHART_ID =
      advanceReportingConstants?.DEFAULT_CHART_IDS?.TOTAL_PASSES_BY_SCHOOLS

    const user = computed(() => store.getters["authentication/user"])
    const isSuperAdmin = computed(
      () => user.value?.role?.name === "admin" && user.value?.auth_type === 1001
    )

    const state = reactive({
      loading: false,
      saving: false,
      selectedFormat:
        advanceReportingConstants?.REPORT_FORMAT_OPTIONS?.[0]?.id || "csv",
      selectedSchools: [],
      startDate: "",
      selectedRepetition: "daily",
      endDate: "",
      selectedCharts: [],
      sendTo: "",
      loadingError: null,
      savingError: null
    })

    const activeSchoolTimeZone = computed(() => {
      return store.getters["schools/activeSchoolTimeZone"]
    })

    const schoolTimeNow = ref(moment().tz(activeSchoolTimeZone.value))
    const schoolTimerId = ref(null)

    const todayOrLaterValidator = (value) => {
      if (!value) return true

      const now = moment().tz(activeSchoolTimeZone.value)
      const isBefore8 = now.hour() < 8
      const minDate = isBefore8
        ? now.startOf("day")
        : now.clone().add(1, "day").startOf("day")

      const selectedDate = moment(value).tz(activeSchoolTimeZone.value)
      if (!selectedDate.isValid()) return false

      return selectedDate.isSameOrAfter(minDate, "day")
    }

    const endDateAfterStartValidator = (value) => {
      if (!value || !state.startDate) return true

      const startDateInState = new Date(
        new Date(state.startDate).setHours(0, 0, 0, 0)
      )
      const startDate = moment(startDateInState).tz(activeSchoolTimeZone.value)
      const endDate = moment(value).tz(activeSchoolTimeZone.value)

      if (!startDate.isValid() || !endDate.isValid()) return false

      return endDate.isAfter(startDate, "day")
    }

    const chartsSelectedValidator = (value) => {
      return Array.isArray(value) && value.length > 0
    }

    const sendToRequiredValidator = (value) => {
      if (!isSuperAdmin.value) return true
      return Boolean(value?.trim?.())
    }

    const sendToFormatValidator = (value) => {
      if (!isSuperAdmin.value) return true
      const username = value?.trim?.()
      if (!username) return true
      if (/[\s,@]/.test(username)) return false
      const fullEmail = `${username}@securly.com`
      const validate = email?.$validator || email
      return typeof validate === "function" ? validate(fullEmail) : false
    }

    const rules = {
      startDate: {
        required: helpers.withMessage("Start date is required", required),
        todayOrLaterValidator: helpers.withMessage(
          "Start date must be today or later (before 8am) or tomorrow (at/after 8am)",
          todayOrLaterValidator
        )
      },
      endDate: {
        required: helpers.withMessage("End date is required", required),
        endDateAfterStartValidator: helpers.withMessage(
          "End date must be after start date",
          endDateAfterStartValidator
        )
      },
      selectedCharts: {
        chartsSelectedValidator: helpers.withMessage(
          "Please select at least one data point",
          chartsSelectedValidator
        )
      },
      sendTo: {
        sendToRequiredValidator: helpers.withMessage(
          "Send to is required",
          sendToRequiredValidator
        ),
        sendToFormatValidator: helpers.withMessage(
          "Email must not contain spaces or commas and must be a valid email address",
          sendToFormatValidator
        )
      }
    }

    const v$ = useVuelidate(rules, state)

    const errors = computed(() => {
      return {
        startDate: v$?.value?.startDate?.$error
          ? v$?.value?.startDate?.$errors
              .map((error) => error.$message)
              .join(", ")
          : "",
        endDate: v$?.value?.endDate?.$error
          ? v$?.value?.endDate?.$errors
              .map((error) => error.$message)
              .join(", ")
          : "",
        selectedCharts: v$?.value?.selectedCharts?.$error
          ? v$?.value?.selectedCharts?.$errors
              .map((error) => error.$message)
              .join(", ")
          : "",
        sendTo: v$?.value?.sendTo?.$error
          ? v$?.value?.sendTo?.$errors.map((error) => error.$message).join(", ")
          : ""
      }
    })

    const REPORT_FORMATS =
      advanceReportingConstants.REPORT_FORMAT_OPTIONS.filter(
        (option) => option.active
      )
    const REPETITION_OPTIONS =
      advanceReportingConstants.REPETITION_OPTIONS.filter(
        (option) => option.active
      )
    const END_OPTIONS = advanceReportingConstants.END_OPTIONS.filter(
      (option) => option.active
    )

    const schools = computed(
      () => store?.getters?.["advanceReportingStore/schoolList"] || []
    )
    const activeSchool = computed(() => schools?.value?.[0] || null)
    const defaultSchoolList = computed(() => {
      // If user has access to multiple schools, default to "All Schools"
      if (schools.value.length > 1) {
        return schools.value.map((school) => ({
          id: school.id,
          name: school.name
        }))
      }
      // If only one school, default to that school
      if (activeSchool.value) {
        return [
          {
            id: activeSchool.value.id,
            name: activeSchool.value.name
          }
        ]
      }
      return []
    })

    const selectedSchool = computed(() => {
      return state.selectedSchools || []
    })

    const selectedRepetitionName = computed(() => {
      const option = REPETITION_OPTIONS.find(
        (opt) => opt.id === state.selectedRepetition
      )
      return option?.name || "Weekly"
    })

    const allChartList = computed(() => {
      return store?.getters?.["advanceReportingStore/allChartList"] || []
    })

    const superAdminAllowedChartId = computed(
      () => SUPER_ADMIN_ALLOWED_CHART_ID
    )

    const isAllChartsSelected = computed(() => {
      return (
        allChartList.value.length > 0 &&
        state.selectedCharts.length === allChartList.value.length
      )
    })

    const isBefore8AM = computed(() => {
      const now = schoolTimeNow.value
      return now.hour() < 8
    })

    /**
     * Get minimum start date based on 8am cutoff
     * Returns today if before 8am, tomorrow if at/after 8am
     */
    const minStartDate = computed(() => {
      const now = schoolTimeNow.value
      if (isBefore8AM.value) {
        return now.startOf("day").toDate()
      }
      return now.clone().add(1, "day").startOf("day").toDate()
    })

    /**
     * Get minimum end date based on start date
     * End date should be at least one day after start date
     */
    const minEndDate = computed(() => {
      const startDate = moment(state.startDate).tz(activeSchoolTimeZone.value)
      return startDate.clone().add(1, "day").startOf("day").toDate()
    })

    /**
     * Get maximum end date (end of current school year - June 30)
     * Returns June 30 of current year if before July, or June 30 of next year if July or later
     */
    const maxEndDate = computed(() => {
      return passHelpers.maxSchoolYearDate()
    })

    /**
     * Get default end date (end of current school year - June 30)
     * Returns June 30 of current year if before July, or June 30 of next year if July or later
     */
    const defaultEndDate = computed(() => {
      return passHelpers.maxSchoolYearDate()
    })

    /**
     * Calculate the next Sunday from a given date
     * @param {moment.Moment} fromDate - The date to calculate from
     * @returns {moment.Moment} The next Sunday
     */
    const getNextSunday = (fromDate) => {
      const daysUntilSunday = (7 - fromDate.day()) % 7
      if (daysUntilSunday === 0) {
        // Date is Sunday, so next Sunday is 7 days away
        return fromDate.clone().add(7, "days")
      }
      // Add days to get to next Sunday
      return fromDate.clone().add(daysUntilSunday, "days")
    }

    /**
     * Get next delivery date for daily repetition
     * @param {moment.Moment} today - Today's date
     * @param {moment.Moment} startDate - The start date
     * @returns {moment.Moment} Next delivery date
     */
    const getNextDailyDeliveryDate = (today, startDate) => {
      const tomorrow = today.clone().add(1, "day")
      // Ensure it's not before the start date
      return tomorrow.isBefore(startDate, "day") ? startDate.clone() : tomorrow
    }

    /**
     * Get next delivery date for weekly repetition (next Sunday)
     * @param {moment.Moment} today - Today's date
     * @param {moment.Moment} startDate - The start date
     * @returns {moment.Moment} Next delivery date
     */
    const getNextWeeklyDeliveryDate = (today, startDate) => {
      const nextSunday = getNextSunday(today)
      // Ensure it's not before the start date
      if (nextSunday.isBefore(startDate, "day")) {
        // Find the Sunday on or after the start date
        return startDate.day() === 0
          ? startDate.clone()
          : getNextSunday(startDate)
      }
      return nextSunday
    }

    /**
     * Get next delivery date for monthly repetition (first day of next month)
     * @param {moment.Moment} today - Today's date
     * @param {moment.Moment} startDate - The start date
     * @returns {moment.Moment} Next delivery date
     */
    const getNextMonthlyDeliveryDate = (today, startDate) => {
      const firstOfNextMonth = today.clone().add(1, "month").startOf("month")
      // Ensure it's not before the start date
      if (firstOfNextMonth.isBefore(startDate, "day")) {
        // Get first day of the month after start date
        return startDate.clone().add(1, "month").startOf("month")
      }
      return firstOfNextMonth
    }

    const nextDeliveryDate = computed(() => {
      if (!state.startDate) return "TBD"

      const today = moment().tz(activeSchoolTimeZone.value)
      const startDate = moment(state.startDate).tz(activeSchoolTimeZone.value)
      let nextDate

      switch (state.selectedRepetition) {
        case "daily":
          nextDate = getNextDailyDeliveryDate(today, startDate)
          break
        case "weekly":
          nextDate = getNextWeeklyDeliveryDate(today, startDate)
          break
        case "monthly":
          nextDate = getNextMonthlyDeliveryDate(today, startDate)
          break
        default:
          // Default to next Sunday
          nextDate = getNextWeeklyDeliveryDate(today, startDate)
      }

      return nextDate.format("MMM D, YYYY")
    })

    const toggleChart = (chartId, checked) => {
      const chart = allChartList.value.find((c) => c.id === chartId)
      const isAllowedChart = chart?.feChartId === SUPER_ADMIN_ALLOWED_CHART_ID
      if (isSuperAdmin.value && !isAllowedChart) {
        return
      }
      if (isSuperAdmin.value && isAllowedChart && !checked) {
        return
      }
      const newSelectedCharts = [...state.selectedCharts]
      if (checked) {
        newSelectedCharts.push(chartId)
      } else {
        const index = newSelectedCharts.indexOf(chartId)
        if (index > -1) {
          newSelectedCharts.splice(index, 1)
        }
      }
      state.selectedCharts = newSelectedCharts
    }

    const toggleSelectAll = () => {
      if (isAllChartsSelected.value) {
        state.selectedCharts = []
      } else {
        state.selectedCharts = allChartList.value.map((chart) => chart.id)
      }
    }

    const handleSchedule = async () => {
      const isFormCorrect = await v$.value.$validate()

      if (!isFormCorrect) {
        return
      }

      try {
        state.saving = true
        state.savingError = null

        const allSchoolsSelected =
          schools.value.length > 1 &&
          state.selectedSchools.length === schools.value.length

        const payload = {
          format: state.selectedFormat,
          schools: allSchoolsSelected
            ? []
            : state.selectedSchools.map((school) => school.id),
          start_date: passHelpers.currTzDate(state.startDate, "YYYY-MM-DD"),
          end_date: passHelpers.currTzDate(state.endDate, "YYYY-MM-DD"),
          repetition: state.selectedRepetition,
          data_points: state.selectedCharts,
          to:
            isSuperAdmin.value && state.sendTo?.trim()
              ? [state.sendTo.trim() + "@securly.com"]
              : store?.getters?.["authentication/getUserId"]
                ? [store?.getters?.["authentication/getUserId"]]
                : []
        }
        await store.dispatch("advanceReportingStore/scheduleReport", payload)
        toast.success("Report scheduled successfully.")
        emit("cancel")
      } catch (error) {
        state.savingError = error
        toast.error(
          error?.response?.data?.message ||
            "Shucks! Something went wrong while scheduling the report."
        )
      } finally {
        state.saving = false
      }
    }

    const handleSchoolChange = (selectedSchoolsArray) => {
      if (selectedSchoolsArray.length === 0) {
        state.selectedSchools = [
          {
            id: activeSchool.value.id,
            name: activeSchool.value.name
          }
        ]
      } else {
        state.selectedSchools = selectedSchoolsArray
      }
    }

    const handleStartDateChange = () => {
      state.endDate = defaultEndDate.value
    }

    const handleCancel = () => {
      emit("cancel")
    }

    const initializeForm = () => {
      const now = moment().tz(activeSchoolTimeZone.value)
      const isBefore8 = now.hour() < 8
      if (isBefore8) {
        state.startDate = now.startOf("day").toDate() // today
      } else {
        state.startDate = now.clone().add(1, "day").startOf("day").toDate() // tomorrow
      }
      state.endDate = defaultEndDate.value
      if (defaultSchoolList.value.length > 0) {
        state.selectedSchools = defaultSchoolList.value
      }
    }

    const getChartSettings = async () => {
      try {
        state.loading = true
        await store.dispatch("advanceReportingStore/fetchChartSettings")
        if (isSuperAdmin.value) {
          const chartList =
            store?.getters?.["advanceReportingStore/allChartList"] || []
          const allowedChart = chartList.find(
            (c) => c.feChartId === SUPER_ADMIN_ALLOWED_CHART_ID
          )
          if (allowedChart) {
            state.selectedCharts = [allowedChart.id]
          }
        }
      } catch (error) {
        state.loadingError = error
      } finally {
        state.loading = false
      }
    }

    const getSchoolList = async () => {
      try {
        state.loading = true
        await store.dispatch("advanceReportingStore/fetchSchoolList")
      } catch (error) {
        state.loadingError = error
      } finally {
        state.loading = false
      }
    }

    const startSchoolTimeInterval = () => {
      schoolTimerId.value = setInterval(() => {
        schoolTimeNow.value = moment().tz(activeSchoolTimeZone.value)
      }, 1000)
    }

    const stopSchoolTimeInterval = () => {
      if (schoolTimerId.value) {
        clearInterval(schoolTimerId.value)
      }
    }

    onMounted(async () => {
      startSchoolTimeInterval()
      initializeForm()
      await getSchoolList()
      await getChartSettings()
    })

    onUnmounted(() => {
      stopSchoolTimeInterval()
    })

    return {
      state,
      v$,
      errors,
      REPORT_FORMATS,
      REPETITION_OPTIONS,
      END_OPTIONS,
      schools,
      selectedSchool,
      selectedRepetitionName,
      allChartList,
      isAllChartsSelected,
      nextDeliveryDate,
      minStartDate,
      minEndDate,
      maxEndDate,
      defaultEndDate,
      isSuperAdmin,
      superAdminAllowedChartId,
      toggleChart,
      toggleSelectAll,
      handleSchedule,
      handleCancel,
      handleSchoolChange,
      handleStartDateChange
    }
  }
}
</script>
